import '../../i18n/autoTranslate';
import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import {
  X,
  Sparkles,
  Settings2,
  MessageSquareText,
  Zap,
  ClipboardCheck,
  BarChart3,
  ShieldCheck,
  Timer,
  FileText,
} from "lucide-react";

/**
 * Premium, detailed “How it works” modal for Interview Setup.
 * - No dependency on shadcn Dialog (safe to drop in any codebase)
 * - Locks body scroll while open
 * - ESC to close
 */
const InterviewSetupHowItWorksModal = ({
  open,
  onClose,
  selectedFormat = "chat",
}) => {
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  const formatLabel =
    selectedFormat === "phone"
      ? "Phone"
      : selectedFormat === "chat"
        ? "Chat"
        : "Session";

  const content = (
    <div
      className="fixed inset-0 z-[999999]"
      role="dialog"
      aria-modal="true"
      aria-label="How InterviewGenieAI works"
    >
      {/* Overlay */}
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute inset-0 bg-black/70 backdrop-blur-[6px]"
      />

      {/* Modal */}
      <div className="absolute inset-0 flex items-stretch justify-stretch p-0">
        <div className="relative w-full h-[100svh] min-h-screen overflow-hidden rounded-none border-x-0 border-y-0 sm:border sm:border-white/10 bg-gradient-to-br from-[rgba(17,24,39,0.78)] via-[rgba(17,24,39,0.58)] to-[rgba(15,23,42,0.40)] shadow-[0_30px_100px_-60px_rgba(99,102,241,0.70)] flex flex-col">
          {/* Top glow */}
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute -top-24 left-1/2 h-64 w-[60rem] -translate-x-1/2 rounded-full bg-gradient-to-r from-violet-500/20 via-sky-500/18 to-emerald-500/16 blur-3xl" />
            <div className="absolute -bottom-28 left-1/2 h-64 w-[56rem] -translate-x-1/2 rounded-full bg-gradient-to-r from-emerald-500/14 via-sky-500/14 to-violet-500/14 blur-3xl" />
          </div>

          {/* Header */}
          <div className="relative flex items-start justify-between gap-4 border-b border-white/10 p-5 sm:p-7 bg-white/[0.02] backdrop-blur-xl">
            <div className="space-y-2">
              <div className="inline-flex flex-wrap items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-xs text-white/70 shadow-[0_0_0_1px_rgba(255,255,255,0.03)] backdrop-blur-xl">
                <span className="h-2 w-2 rounded-full bg-sky-300 shadow-[0_0_16px_rgba(125,211,252,0.55)]" />
                How it works • {formatLabel} format • Real-time feedback
                <span className="ml-1 rounded-full bg-white/10 px-2 py-0.5 text-[10px] text-white/70">
                  End-to-end
                </span>
              </div>
              <div className="text-2xl sm:text-3xl font-semibold tracking-tight text-white">
                Your interview, upgraded into a guided training loop.
              </div>
              <div className="max-w-2xl text-sm text-white/60">
                You configure the session once. Then the AI runs a realistic interviewer flow — question → answer →
                instant feedback → follow-up — and finishes with a structured report you can use to improve fast.
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="shrink-0 inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-white/10 bg-white/5 text-white/80 hover:bg-white/10 hover:text-white transition"
              aria-label="Close modal"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Body */}
          <div className="relative grid grid-cols-1 lg:grid-cols-5 gap-0 flex-1 overflow-y-auto overscroll-contain">
            {/* Left: Steps */}
            <div className="lg:col-span-3 p-5 sm:p-7">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <InfoCard
                  icon={Settings2}
                  title="1) Setup (2–3 minutes)"
                  items={[
                    "Pick interview type (behavioral / technical / case / stress / panel)",
                    "Target role + industry + company style",
                    "Tune AI personality (tone, difficulty, pace, pressure)",
                    "Select question categories + follow-ups",
                    "Choose format (Chat / Phone) + duration",
                  ]}
                />

                <InfoCard
                  icon={MessageSquareText}
                  title="2) Live interview flow"
                  items={[
                    "The AI asks a realistic question",
                    "You respond (typed in Chat or spoken in Phone)",
                    "Instant feedback appears after every submission",
                    "The AI adapts the next question based on your answer",
                  ]}
                />

                <InfoCard
                  icon={Zap}
                  title="3) Real-time feedback (every answer)"
                  items={[
                    "A score + quick verdict (clear / unclear / missing signals)",
                    "Strengths + what to improve next",
                    "STAR coaching when relevant (Situation/Task/Action/Result)",
                    "Suggested re-phrasing (more confident + concise)",
                    "Follow-up questions that feel human",
                  ]}
                />

                <InfoCard
                  icon={ClipboardCheck}
                  title="4) End of session → final results"
                  items={[
                    "Overall score + category breakdown",
                    "Top strengths + weakest areas",
                    "Action plan (what to practice next)",
                    "Session transcript + key moments",
                  ]}
                />
              </div>

              {/* What we provide */}
              <div className="mt-5 rounded-3xl border border-white/10 bg-white/[0.03] p-4 sm:p-5 backdrop-blur-xl">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.04]">
                    <Sparkles className="h-5 w-5 text-white/85" />
                  </div>
                  <div className="space-y-2">
                    <div className="text-sm font-semibold text-white">What you get (deliverables)</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[12px] text-white/65">
                      <Pill icon={BarChart3} text="Scores: overall + per competency" />
                      <Pill icon={FileText} text="Structured report: strengths, gaps, next steps" />
                      <Pill icon={MessageSquareText} text="Full conversation transcript" />
                      <Pill icon={ShieldCheck} text="Consistent rubric (fair scoring)" />
                      <Pill icon={Timer} text="Timing & pacing signals" />
                      <Pill icon={Zap} text="Instant coaching after each answer" />
                    </div>
                    <div className="text-[12px] text-white/55 leading-relaxed">
                      <span className="text-white/70 font-medium">Format:</span> Each question is delivered with clear
                      intent, and each feedback block follows a consistent structure (score → strengths → missing signals
                      → improvement suggestions → next question/follow-up). Your final results bundle everything into a
                      clean summary you can reuse in analytics.
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Conversation format preview */}
            <div className="lg:col-span-2 border-t lg:border-t-0 lg:border-l border-white/10 p-5 sm:p-7">
              <div className="space-y-3">
                <div className="text-sm font-semibold text-white">Conversation format preview</div>
                <div className="text-xs text-white/60">
                  This is the exact loop the session follows: {" "}
                  <span className="text-white/75">Question → Your Answer → Instant Feedback → Next</span>
                </div>

                <div className="mt-3 space-y-3">
                  <Bubble role="ai" title="AI Interviewer" badge="Question">
                    Tell me about a time you handled a difficult stakeholder.
                  </Bubble>
                  <Bubble role="user" title="You" badge="Answer">
                    I aligned expectations by clarifying priorities, proposing a timeline, and sharing progress updates.
                  </Bubble>
                  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 backdrop-blur-xl">
                    <div className="flex items-start justify-between gap-3">
                      <div className="text-xs font-semibold text-white">Instant Feedback</div>
                      <div className="text-[10px] rounded-full bg-white/10 px-2 py-0.5 text-white/70">Score: 7.5/10</div>
                    </div>
                    <div className="mt-2 space-y-2 text-[12px] text-white/65">
                      <div>
                        <span className="text-white/75 font-medium">Strong:</span> Clear ownership, good communication focus.
                      </div>
                      <div>
                        <span className="text-white/75 font-medium">Missing:</span> Add the impact + measurable result.
                      </div>
                      <div>
                        <span className="text-white/75 font-medium">Improve:</span> Use STAR: situation, action, and outcome in 2–3 lines.
                      </div>
                    </div>
                  </div>
                  <Bubble role="ai" title="AI Interviewer" badge="Follow-up">
                    What was the outcome, and how did you measure success?
                  </Bubble>
                </div>

                <div className="mt-5 rounded-2xl border border-white/10 bg-white/[0.03] p-4 backdrop-blur-xl">
                  <div className="text-xs font-semibold text-white">Pro answer format (recommended)</div>
                  <div className="mt-2 text-[12px] text-white/60 leading-relaxed">
                    Keep answers {" "}
                    <span className="text-white/75 font-medium">structured</span> and {" "}
                    <span className="text-white/75 font-medium">scannable</span>:
                    <ul className="mt-2 list-disc pl-4 space-y-1">
                      <li>
                        <span className="text-white/75 font-medium">Situation/Task</span>: 1 short line
                      </li>
                      <li>
                        <span className="text-white/75 font-medium">Action</span>: 2–4 key actions (bullets are fine)
                      </li>
                      <li>
                        <span className="text-white/75 font-medium">Result</span>: numbers, impact, what changed
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="mt-5 flex flex-col gap-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-violet-500/90 via-sky-500/90 to-emerald-500/90 px-4 py-2.5 text-sm font-semibold text-white shadow-[0_18px_50px_-25px_rgba(99,102,241,0.65)] hover:brightness-105 active:brightness-95 transition"
                  >
                    Got it — continue setup
                  </button>
                  <div className="text-[11px] text-white/50 text-center">
                    Tip: Start shorter first, then increase difficulty as you improve.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

const InfoCard = ({ icon: Icon, title, items }) => {
  return (
    <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-4 sm:p-5 backdrop-blur-xl">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.04]">
          <Icon className="h-5 w-5 text-white/85" />
        </div>
        <div className="space-y-2">
          <div className="text-sm font-semibold text-white">{title}</div>
          <ul className="space-y-1 text-[12px] text-white/60">
            {items?.map((t, idx) => (
              <li key={idx} className="leading-relaxed">
                <span className="mr-2 text-white/40">•</span>
                {t}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

const Pill = ({ icon: Icon, text }) => (
  <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 backdrop-blur-xl">
    <Icon className="h-3.5 w-3.5 text-white/70" />
    <span className="text-[11px] text-white/65">{text}</span>
  </div>
);

const Bubble = ({ role = "ai", title, badge, children }) => {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div className="max-w-[92%] rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 backdrop-blur-xl shadow-[0_0_0_1px_rgba(255,255,255,0.02)]">
        <div className="flex items-center justify-between gap-3">
          <div className="text-[11px] font-semibold text-white/85">{title}</div>
          <div className="text-[10px] rounded-full bg-white/10 px-2 py-0.5 text-white/70">{badge}</div>
        </div>
        <div className="mt-1 text-[12px] text-white/65 leading-relaxed">{children}</div>
      </div>
    </div>
  );
};

export default InterviewSetupHowItWorksModal;
